import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, MapPin, ShieldCheck, Sparkles } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import BrandTicker from "@/components/BrandTicker";
import ServiceCard from "@/components/ServiceCard";
import { useLang } from "@/contexts/LanguageContext";
import useJsonLd from "@/hooks/useJsonLd";
import { SITE, SERVICES, MEDIA } from "@/data/site";
import { AREA_PILL_TO_SLUG } from "@/data/serviceAreas";

const Home = () => {
  const { t, lang } = useLang();
  const faqs = t("home.faqs");

  // FAQPage JSON-LD — reacts to language switch
  useJsonLd(
    `faq-${lang}`,
    Array.isArray(faqs) && faqs.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          inLanguage: lang === "es" ? "es" : "en",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: f.a,
            },
          })),
        }
      : null,
  );

  return (
    <div data-testid="page-home">
      {/* HERO — organic split */}
      <section className="relative overflow-hidden">
        {/* Subtle Hawaii landscape watermark with fade-to-white top + bottom */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none z-0"
        >
          <img
            src={MEDIA.hawaii}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ filter: "blur(28px)", opacity: 0.09, transform: "scale(1.08)" }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, #FAFAFA 0%, rgba(250,250,250,0.55) 22%, rgba(250,250,250,0.35) 50%, rgba(250,250,250,0.7) 80%, #FAFAFA 100%)",
            }}
          />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pt-12 sm:pt-20 lg:pt-24 pb-16 sm:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Left: text */}
            <div className="lg:col-span-6 animate-fade-up">
              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#082133]/10 px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] text-[#082133] font-semibold">
                <Sparkles className="h-3.5 w-3.5" />
                {t("home.eyebrow")}
              </div>

              <h1
                data-testid="hero-title"
                className="mt-8 font-display font-semibold text-[#111111] text-[44px] leading-[1.02] sm:text-[60px] lg:text-[74px] tracking-[-0.03em]"
              >
                {t("home.heroTitle")}
              </h1>

              <p className="mt-6 max-w-xl text-[17px] sm:text-[18px] leading-relaxed text-[#4B5563]">
                {t("home.heroLead")}
              </p>

              <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Link to="/contact">
                  <Button
                    data-testid="hero-cta-primary"
                    className="w-full sm:w-auto rounded-full h-14 px-8 bg-[#082133] text-[#FAFAFA] hover:bg-[#0A2D47] text-[15px] font-medium tracking-wide shadow-[0_18px_40px_-15px_rgba(8,33,51,0.55)]"
                  >
                    {t("home.ctaPrimary")}
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button
                    data-testid="hero-cta-secondary"
                    variant="outline"
                    className="w-full sm:w-auto rounded-full h-14 px-8 border-[#082133]/20 bg-white text-[#082133] hover:bg-[#082133]/5 text-[15px] font-medium tracking-wide"
                  >
                    {t("home.ctaSecondary")}
                  </Button>
                </Link>
              </div>

              <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-5">
                <a
                  href={SITE.phoneHref}
                  data-testid="hero-phone-link"
                  className="inline-flex items-center gap-2 text-[#111111] text-[15px] font-medium hover:text-[#082133]"
                >
                  <span className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-[#DCE3DE]">
                    <Phone className="h-4 w-4 text-[#082133]" />
                  </span>
                  {SITE.phone}
                </a>
                <div className="inline-flex items-center gap-2 text-[#4B5563] text-[14px]">
                  <ShieldCheck className="h-4 w-4 text-[#082133]" />
                  {lang === "en"
                    ? "Licensed · Insured · OEM Parts"
                    : "Licenciado · Asegurado · Piezas OEM"}
                </div>
              </div>
            </div>

            {/* Right: organic image */}
            <div className="lg:col-span-6 relative">
              <div className="relative aspect-[5/4] w-full max-w-[720px] mx-auto lg:ml-auto lg:mr-0">
                <div className="absolute -top-8 -left-8 h-44 w-44 rounded-full bg-[#DCE3DE] blur-2xl opacity-70" />
                <div className="absolute -bottom-10 -right-8 h-56 w-56 rounded-full bg-[#082133]/10 blur-3xl" />

                <div className="relative h-full w-full overflow-hidden blob-mask-wide shadow-[0_55px_110px_-30px_rgba(8,33,51,0.4)]">
                  <img
                    src={MEDIA.hero}
                    alt="Genesis Appliance Repair technician servicing a washer on the Big Island of Hawaii"
                    className="h-full w-full object-cover object-center"
                  />
                </div>

                {/* Floating mini card */}
                <div className="absolute -bottom-6 left-4 sm:left-8 rounded-2xl bg-white border border-[#E5E7EB] shadow-[0_24px_50px_-20px_rgba(8,33,51,0.25)] px-5 py-4 max-w-[240px]">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#4B5563] font-semibold">
                    {t("common.proudlyServing")}
                  </div>
                  <div className="mt-1.5 font-display text-[15px] text-[#111111] font-semibold flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-[#082133]" />
                    {SITE.region}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BRAND TICKER */}
      <BrandTicker />

      {/* SERVICES OVERVIEW */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-24 sm:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-14">
          <div className="lg:col-span-7">
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#082133] font-semibold">
              {t("home.sectionServicesEyebrow")}
            </span>
            <h2 className="mt-4 font-display font-semibold text-[#111111] text-[34px] sm:text-[44px] lg:text-[52px] leading-[1.05] tracking-[-0.025em]">
              {t("home.sectionServicesTitle")}
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[16px] leading-relaxed text-[#4B5563]">
              {t("home.sectionServicesLead")}
            </p>
            <Link
              to="/services"
              data-testid="home-view-all-services"
              className="mt-5 inline-flex items-center gap-2 text-[#082133] font-medium hover:gap-3 transition-all"
            >
              {t("common.viewAllServices")}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Asymmetric grid */}
        <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-6">
          <ServiceCard service={SERVICES[0]} span="md:col-span-3 lg:col-span-5" />
          <ServiceCard service={SERVICES[1]} span="md:col-span-3 lg:col-span-4" />
          <ServiceCard service={SERVICES[2]} span="md:col-span-6 lg:col-span-3" />
          <ServiceCard service={SERVICES[3]} span="md:col-span-3 lg:col-span-6" />
          <ServiceCard service={SERVICES[4]} span="md:col-span-3 lg:col-span-6" />
        </div>
      </section>

      {/* SERVICE AREAS BAND */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pb-12">
        <div className="rounded-[32px] bg-[#082133] text-[#FAFAFA] px-8 sm:px-12 py-12 sm:py-16 relative overflow-hidden grain">
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-5">
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#FAFAFA]/60 font-semibold">
                {t("common.serviceAreas")}
              </span>
              <h3 className="mt-3 font-display text-3xl sm:text-4xl font-semibold leading-tight">
                {SITE.region}
              </h3>
            </div>
            <div className="md:col-span-7">
              <div className="flex flex-wrap gap-2.5">
                {SITE.serviceAreas.map((a) => {
                  const slug = AREA_PILL_TO_SLUG[a];
                  const testId = `area-pill-${a.replace(/[ ,]+/g, "-").toLowerCase()}`;
                  const baseClasses =
                    "rounded-full border border-[#FAFAFA]/20 px-4 py-2 text-sm tracking-wide text-[#FAFAFA]/90 transition";
                  if (slug) {
                    return (
                      <Link
                        key={a}
                        to={`/service-areas/${slug}`}
                        data-testid={testId}
                        className={`${baseClasses} hover:bg-[#FAFAFA] hover:text-[#082133]`}
                      >
                        {a}
                      </Link>
                    );
                  }
                  return (
                    <span key={a} data-testid={testId} className={baseClasses}>
                      {a}
                    </span>
                  );
                })}
              </div>
              <p className="mt-6 text-[14px] text-[#FAFAFA]/65">
                {t("common.seeAllAreas")}.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-24 sm:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#082133] font-semibold">
              {t("home.faqEyebrow")}
            </span>
            <h2 className="mt-4 font-display font-semibold text-[#111111] text-[34px] sm:text-[44px] lg:text-[52px] leading-[1.05] tracking-[-0.025em]">
              {t("home.faqTitle")}
            </h2>
            <div className="hidden lg:block mt-10 relative aspect-square w-full max-w-sm">
              <div className="absolute inset-0 rounded-[40%_60%_50%_50%/55%_45%_55%_45%] overflow-hidden">
                <img
                  src={MEDIA.hawaii}
                  alt="Hawaii landscape"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <Accordion
              data-testid="home-faq-accordion"
              type="single"
              collapsible
              className="w-full"
              defaultValue="item-0"
            >
              {(faqs || []).map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="border-b border-[#E5E7EB] last:border-b-0"
                >
                  <AccordionTrigger className="py-6 text-left font-display text-lg sm:text-xl font-medium text-[#111111] hover:no-underline hover:text-[#082133]">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-7 text-[15px] leading-relaxed text-[#4B5563]">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Block */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pb-24 sm:pb-32">
        <div className="rounded-[32px] border border-[#E5E7EB] bg-white px-8 sm:px-12 py-14 sm:py-20 shadow-[0_30px_60px_-30px_rgba(8,33,51,0.15)] relative overflow-hidden">
          <div className="absolute -right-20 -bottom-20 h-72 w-72 rounded-full bg-[#DCE3DE]/60 blur-3xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 items-end gap-10">
            <div className="lg:col-span-8">
              <h3 className="font-display font-semibold text-[#111111] text-[30px] sm:text-[40px] leading-[1.1] tracking-[-0.02em]">
                {t("home.ctaBlockTitle")}
              </h3>
              <p className="mt-4 max-w-xl text-[#4B5563] text-[16px] leading-relaxed">
                {t("home.ctaBlockLead")}
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3">
              <Link to="/contact" className="block">
                <Button
                  data-testid="cta-block-book"
                  className="w-full rounded-full h-14 bg-[#082133] text-[#FAFAFA] hover:bg-[#0A2D47] text-[15px] font-medium tracking-wide"
                >
                  {t("common.bookOnline")}
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a
                href={SITE.phoneHref}
                data-testid="cta-block-call"
                className="inline-flex items-center justify-center gap-2 rounded-full h-14 px-6 border border-[#082133]/20 bg-white text-[#082133] hover:bg-[#082133]/5 text-[15px] font-medium"
              >
                <Phone className="h-4 w-4" />
                {SITE.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
